// src/pages/personal/analytics/MarketingExpensesSection/components/SourceDistribution.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import { DistributionItem } from '../hooks/useMarketingExpensesData';
import { formatPercent, formatPrice, formatNumber } from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import styles from '../MarketingExpensesSection.module.css';

interface SourceDistributionProps {
  title: string;
  data: DistributionItem[];
  isLoading: boolean;
  type: 'channel' | 'platform';
}

const SourceDistribution: React.FC<SourceDistributionProps> = ({
  title,
  data,
  isLoading,
  type
}) => {
  const { t, i18n } = useTranslation(['expenses', 'common']);
  const { currencySymbol } = useBusinessCurrency();
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Проверка наличия данных
  const hasData = data && data.length > 0;
  
  // Функция для получения перевода названия источника
  const getSourceName = (sourceId: string): string => {
    if (type === 'channel') {
      return t(`marketing.sources.${sourceId}`, { defaultValue: sourceId });
    } else {
      return t(`marketing.platforms.${sourceId}`, { defaultValue: sourceId });
    }
  };

  // Подготовка данных для графика
  const chartData = hasData 
    ? data.map(item => ({
        ...item,
        label: getSourceName(item.name),
      }))
    : [];
  
  return (
    <Card padding="none" shadow="sm">
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <p className={styles.cardSubtitle}>
          {t('marketing.expenses.distribution_subtitle')}
        </p>
      </div>
      <div className={styles.cardContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>{t('common:loading')}</p>
          </div>
        ) : !hasData ? (
          <div className={styles.noDataContainer}>
            <p>{t('common:no_data')}</p>
          </div>
        ) : (
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color} 
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [
                    formatPrice(value, currencySymbol, currentLocale),
                    t('marketing.expenses.spend')
                  ]}
                  labelFormatter={(name) => name}
                />
                <Legend
                  formatter={(value, _, index) => {
                    const item = chartData[index];
                    return (
                      <span className="text-sm">
                        {value} ({formatPercent(item.percentage / 100, 1, true, currentLocale)})
                      </span>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Таблица с данными */}
            <div className="px-4 pb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">{t('marketing.expenses.source')}</th>
                    <th className="text-right py-2">{t('marketing.expenses.amount')}</th>
                    <th className="text-right py-2">{t('marketing.expenses.percentage')}</th>
                    <th className="text-right py-2">{t('marketing.metrics.impressions')}</th>
                    <th className="text-right py-2">{t('marketing.metrics.clicks')}</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 flex items-center">
                        <span 
                          className="w-3 h-3 mr-2 rounded-sm inline-block"
                          style={{ backgroundColor: item.color }}
                        ></span>
                        {item.label}
                      </td>
                      <td className="text-right py-2">
                        {formatPrice(item.value, currencySymbol, currentLocale)}
                      </td>
                      <td className="text-right py-2">
                        {formatPercent(item.percentage / 100, 1, true, currentLocale)}
                      </td>
                      <td className="text-right py-2">
                        {formatNumber(item.impressions || 0, undefined, true, currentLocale)}
                      </td>
                      <td className="text-right py-2">
                        {formatNumber(item.clicks || 0, undefined, true, currentLocale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SourceDistribution;